import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

export const activityActionLabels = {
  preview: 'Preview',
  pdf_export: 'PDF Export',
  browser_print: 'Browser Print',
  thermal_print: 'Thermal Print',
  email: 'Email',
  whatsapp: 'WhatsApp',
  reprint: 'Reprint',
  download: 'Download',
};

export const activityResultLabels = {
  success: 'Success',
  requested: 'Requested',
  generated: 'Generated',
  failed: 'Failed',
  started: 'Started',
  cancelled: 'Cancelled',
};

export const documentTypeLabels = {
  kitchen_ticket: 'Kitchen Ticket',
  receipt: 'Customer Receipt',
};

export const formatActivityTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

export default function useOrderDocumentWorkflow({ apiBaseUrl, selectedOrderId }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [previewOrderId, setPreviewOrderId] = useState(null);
  const [previewDocumentType, setPreviewDocumentType] = useState('kitchen_ticket');
  const [activityLoading, setActivityLoading] = useState(false);
  const [documentActivities, setDocumentActivities] = useState([]);
  const thermalPreviewRef = useRef(null);
  const previewDocumentRef = useRef(null);
  const previewOrderIdRef = useRef(null);
  const previewDocumentTypeRef = useRef('kitchen_ticket');

  const fetchDocumentPreviewDocument = async (orderId, documentType, { openModal = false } = {}) => {
    setPreviewLoading(true);
    setPreviewDocument(null);
    previewDocumentRef.current = null;
    thermalPreviewRef.current = null;
    if (openModal) setPreviewOpen(true);
    try {
      const { data } = await axios.get(`${apiBaseUrl}/orders/${orderId}/documents/${documentType}/preview`, {
        withCredentials: true,
      });
      setPreviewDocument(data);
      previewDocumentRef.current = data;
      setPreviewOrderId(orderId);
      previewOrderIdRef.current = orderId;
      setPreviewDocumentType(documentType);
      previewDocumentTypeRef.current = documentType;
      return data;
    } catch (error) {
      if (openModal) setPreviewOpen(false);
      toast.error(`Failed to load ${documentTypeLabels[documentType] || 'document'} preview`);
      return null;
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchDocumentActivities = useCallback(async (orderId) => {
    if (!orderId) {
      setDocumentActivities([]);
      return;
    }
    setActivityLoading(true);
    try {
      const { data } = await axios.get(`${apiBaseUrl}/orders/${orderId}/document-activities`, {
        withCredentials: true,
      });
      setDocumentActivities(data);
    } catch (error) {
      setDocumentActivities([]);
    } finally {
      setActivityLoading(false);
    }
  }, [apiBaseUrl]);

  const recordDocumentActivity = async ({ orderId, documentType, actionType, result, details = {} }) => {
    if (!orderId) return;
    try {
      await axios.post(
        `${apiBaseUrl}/document-activities`,
        {
          document_type: documentType,
          order_id: orderId,
          action_type: actionType,
          result,
          details,
        },
        { withCredentials: true }
      );
      if (selectedOrderId === orderId) {
        await fetchDocumentActivities(orderId);
      }
    } catch (error) {
      // Keep core print actions non-blocking even if telemetry fails.
    }
  };

  useEffect(() => {
    if (!selectedOrderId) {
      setDocumentActivities([]);
      return;
    }
    fetchDocumentActivities(selectedOrderId);
  }, [fetchDocumentActivities, selectedOrderId]);

  const openDocumentPreview = async (orderId, documentType) => {
    await fetchDocumentPreviewDocument(orderId, documentType, { openModal: true });
    await recordDocumentActivity({
      orderId,
      documentType,
      actionType: 'preview',
      result: 'success',
      details: { channel: 'order_details_preview_button' },
    });
  };

  const refreshDocumentPreview = async () => {
    if (!previewOrderId || !previewDocumentType) return;
    await fetchDocumentPreviewDocument(previewOrderId, previewDocumentType);
  };

  const saveAsPdf = async () => {
    const currentPreviewDocument = previewDocumentRef.current;
    const currentPreviewOrderId = previewOrderIdRef.current;
    const currentDocumentType = previewDocumentTypeRef.current;
    if (!currentPreviewDocument || !thermalPreviewRef.current) {
      toast.error('Preview document is not ready');
      return;
    }

    setPdfSaving(true);
    try {
      const element = thermalPreviewRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });

      const imageData = canvas.toDataURL('image/png');
      const paperWidthMm = currentPreviewDocument.paper_width === '58mm' ? 58 : 80;
      const contentHeightMm = (canvas.height * paperWidthMm) / canvas.width;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [paperWidthMm, contentHeightMm],
      });

      pdf.addImage(imageData, 'PNG', 0, 0, paperWidthMm, contentHeightMm, undefined, 'FAST');
      const baseName = currentDocumentType === 'receipt' ? 'receipt' : 'kitchen-ticket';
      pdf.save(`${currentPreviewDocument.order_number || baseName}.pdf`);
      await recordDocumentActivity({
        orderId: currentPreviewOrderId,
        documentType: currentDocumentType,
        actionType: 'pdf_export',
        result: 'generated',
        details: { paper_width: currentPreviewDocument.paper_width || '80mm', document_type: currentDocumentType },
      });
    } catch (error) {
      await recordDocumentActivity({
        orderId: currentPreviewOrderId,
        documentType: currentDocumentType,
        actionType: 'pdf_export',
        result: 'failed',
        details: { reason: 'pdf_export_error', document_type: currentDocumentType },
      });
      toast.error('Failed to save PDF');
    } finally {
      setPdfSaving(false);
    }
  };

  const browserPrint = async () => {
    const currentPreviewDocument = previewDocumentRef.current;
    const currentPreviewOrderId = previewOrderIdRef.current;
    const currentDocumentType = previewDocumentTypeRef.current;
    if (!currentPreviewDocument || !thermalPreviewRef.current) {
      toast.error('Preview document is not ready');
      return;
    }

    setPrintLoading(true);
    const paperWidth = currentPreviewDocument.paper_width === '58mm' ? '58mm' : '80mm';
    const printFrame = document.createElement('iframe');
    printFrame.title = 'Print document';
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = paperWidth;
    printFrame.style.height = '1px';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';
    document.body.appendChild(printFrame);

    const printDocument = printFrame.contentDocument;
    const printWindow = printFrame.contentWindow;
    if (!printDocument || !printWindow) {
      printFrame.remove();
      setPrintLoading(false);
      toast.error('Failed to prepare print document');
      return;
    }

    const documentStyles = Array.from(
      document.head.querySelectorAll('link[rel="stylesheet"], style')
    ).map((node) => node.outerHTML).join('\n');
    printDocument.open();
    printDocument.write(`
      <!doctype html>
      <html>
        <head>
          <title>${currentPreviewDocument.order_number || 'Kitchen Ticket'}</title>
          ${documentStyles}
          <style>
            @page { size: ${paperWidth} auto; margin: 0; }
            html, body {
              width: ${paperWidth} !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              background: #fff !important;
            }
            [data-testid="thermal-document-preview"] {
              box-sizing: border-box !important;
              width: ${paperWidth} !important;
              max-width: ${paperWidth} !important;
              margin: 0 !important;
              padding: 2mm 2mm 3mm !important;
              border: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
            }
          </style>
        </head>
        <body>${thermalPreviewRef.current.outerHTML}</body>
      </html>
    `);
    printDocument.close();

    await Promise.all(
      Array.from(printDocument.querySelectorAll('link[rel="stylesheet"]')).map((link) => (
        link.sheet
          ? Promise.resolve()
          : new Promise((resolve) => {
              link.addEventListener('load', resolve, { once: true });
              link.addEventListener('error', resolve, { once: true });
            })
      ))
    );
    if (printDocument.fonts?.ready) {
      await printDocument.fonts.ready;
    }
    await Promise.all(
      Array.from(printDocument.images).map((image) => (
        image.complete ? Promise.resolve() : image.decode().catch(() => undefined)
      ))
    );

    const cleanup = () => {
      printFrame.remove();
      setPrintLoading(false);
      printWindow.removeEventListener('afterprint', cleanup);
    };

    printWindow.addEventListener('afterprint', cleanup);
    try {
      printWindow.focus();
      printWindow.print();
      await recordDocumentActivity({
        orderId: currentPreviewOrderId,
        documentType: currentDocumentType,
        actionType: 'browser_print',
        result: 'requested',
        details: {
          paper_width: currentPreviewDocument.paper_width || '80mm',
          document_type: currentDocumentType,
          certainty: 'browser_print_invoked',
        },
      });
      setTimeout(cleanup, 3000);
    } catch (error) {
      cleanup();
      toast.error('Failed to open browser print');
    }
  };

  const queueDocumentAction = async (orderId, documentType, action) => {
    if (!orderId || !documentType || !action) return;
    const document = await fetchDocumentPreviewDocument(orderId, documentType, { openModal: true });
    if (!document) return;

    const previewMountDeadline = Date.now() + 5000;
    while (!thermalPreviewRef.current && Date.now() < previewMountDeadline) {
      await new Promise((resolve) => setTimeout(resolve, 25));
    }

    if (!thermalPreviewRef.current) {
      toast.error('Preview document is not ready');
      return;
    }
    if (action === 'pdf') await saveAsPdf();
    if (action === 'print') await browserPrint();
  };

  const handleReprint = async (orderId, documentType) => {
    const document = await fetchDocumentPreviewDocument(orderId, documentType, { openModal: true });
    if (!document) return;
    await recordDocumentActivity({
      orderId,
      documentType,
      actionType: 'preview',
      result: 'success',
      details: { channel: 'open_again_button', document_type: documentType },
    });
  };

  const hasDocumentActivity = (documentType) => {
    return documentActivities.some((activity) => activity.document_type === documentType);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
    previewDocumentRef.current = null;
    setPreviewOrderId(null);
    previewOrderIdRef.current = null;
    setPreviewDocumentType('kitchen_ticket');
    previewDocumentTypeRef.current = 'kitchen_ticket';
  };

  return {
    previewOpen,
    setPreviewOpen,
    previewLoading,
    previewDocument,
    pdfSaving,
    printLoading,
    previewDocumentType,
    activityLoading,
    documentActivities,
    thermalPreviewRef,
    openDocumentPreview,
    queueDocumentAction,
    refreshDocumentPreview,
    saveAsPdf,
    browserPrint,
    handleReprint,
    hasDocumentActivity,
    closePreview,
  };
}
